import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Divider, Paper, List, ListItemButton, ListItemIcon,
  ListItemText, Avatar, Switch, IconButton, TextField, Button,
  CircularProgress, Drawer, useMediaQuery, useTheme, Stack, Chip,
  Alert, alpha, Tooltip,
} from "@mui/material";
import {
  Edit, Lock, Language, Security, Notifications, Help, Info,
  ContactMail, Policy, Gavel, ChevronRight, MailOutline, Phone,
  TwoWheeler, ScheduleOutlined, ViewCarousel, Menu as MenuIcon,
  Close as CloseIcon, Settings as SettingsIcon,
  PhotoCamera, Delete as DeleteIcon, CloudUpload,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorDark: "#B91C1C",
  errorSoft: "#FEE2E2",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

// ── Helper: build a full URL from a server-stored image path ─────────────────
// Backend returns relative paths like "uploads/avatars/123.jpg".
// We need full URLs for <img src>. Also handles backslashes from Windows hosts.
const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;
  return `${API}/${String(path).replace(/\\/g, "/").replace(/^\//, "")}`;
};

// ── Shared helper components ───────────────────────────────────────────────────
const ProfileItem = ({ label, value, multiline = false }) => (
  <Box>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 0.5, textTransform: "uppercase", mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ whiteSpace: multiline ? "pre-line" : "normal", fontWeight: 600, fontSize: { xs: 14, sm: 15 }, color: C.textPrimary }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const SectionPaper = ({ children, accent = "primary", ...props }) => (
  <Paper elevation={0} sx={{
    position: "relative",
    p: { xs: 2.5, sm: 3 },
    borderRadius: 3,
    border: `1px solid ${C.border}`,
    bgcolor: C.paper,
    mb: 2.5,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0, left: 0, right: 0,
      height: "3px",
      background: accent === "secondary"
        ? `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`
        : `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
    },
    ...props.sx,
  }}>
    {children}
  </Paper>
);

const ToggleRow = ({ label, description, checked, onChange }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25 }}>
    <Box flex={1} pr={2}>
      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, color: C.textPrimary }}>{label}</Typography>
      {description && <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.25, lineHeight: 1.5 }}>{description}</Typography>}
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      size="small"
      sx={{
        "& .Mui-checked": { color: `${C.primary} !important` },
        "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${C.primary} !important` },
      }}
    />
  </Box>
);

const InfoCard = ({ icon, title, description }) => (
  <Paper elevation={0} sx={{
    p: 2, borderRadius: 2.5,
    border: `1px solid ${C.borderSoft}`,
    bgcolor: C.paper,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": { bgcolor: C.primarySofter, borderColor: C.primary, transform: "translateX(2px)" },
  }}>
    {icon && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: description ? 0.5 : 0 }}>
        {icon}
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, color: C.textPrimary }}>{title}</Typography>
      </Box>
    )}
    {!icon && (
      <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, mb: description ? 0.75 : 0, color: C.textPrimary }}>{title}</Typography>
    )}
    {description && (
      <Typography sx={{ fontSize: 12, color: C.textSecondary, pl: icon ? 4 : 0, lineHeight: 1.5 }}>{description}</Typography>
    )}
  </Paper>
);

const PrimaryButton = (props) => (
  <Button
    variant="contained"
    {...props}
    sx={{
      borderRadius: 2.5,
      textTransform: "none",
      fontWeight: 700,
      background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
      boxShadow: "none",
      "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
      "&.Mui-disabled": { background: "#CCC", color: "#fff" },
      ...props.sx,
    }}
  />
);

// ── Avatar Uploader ──────────────────────────────────────────────────────────
// Big circular avatar with hover overlay (camera icon) + remove button.
// Handles the file pick → preview → backend POST flow.
function AvatarUploader({
  currentImage,    // current saved profile image path
  fallbackName,    // for initials fallback
  onUploadSuccess, // (newPath) => void — called after backend confirms save
  onRemoveSuccess, // () => void
  userId,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const displayImage = previewUrl || resolveImageUrl(currentImage);

  const initials = (fallbackName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const triggerFilePick = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setError("Image must be under 3MB.");
      return;
    }

    setError("");
    setSuccess("");

    // Show preview immediately (don't wait for upload)
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    // Upload to backend
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("avatar", file);
      formData.append("userId", userId);

      const res = await axios.post(`${API}/api/user/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newPath = res.data?.profileImage || res.data?.user?.profileImage || "";

      // Update localStorage so the topbar/sidenav reflect the new avatar everywhere
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        stored.profileImage = newPath;
        localStorage.setItem("user", JSON.stringify(stored));
      } catch { /* localStorage failure is non-fatal */ }

      setSuccess("Profile picture updated.");
      setPreviewUrl(""); // drop the blob; we'll use the server URL now
      onUploadSuccess?.(newPath);
    } catch (err) {
      setPreviewUrl(""); // revert preview on failure
      setError(err.response?.data?.error || err.response?.data?.msg || "Upload failed.");
    } finally {
      setUploading(false);
      // Clear the file input so picking the same file twice still triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!currentImage) return;
    if (!window.confirm("Remove your profile picture?")) return;

    try {
      setUploading(true);
      setError("");
      await axios.delete(`${API}/api/user/avatar`, { data: { userId } });

      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        stored.profileImage = "";
        localStorage.setItem("user", JSON.stringify(stored));
      } catch { /* non-fatal */ }

      setSuccess("Profile picture removed.");
      onRemoveSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove picture.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        style={{ display: "none" }}
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ xs: "center", sm: "flex-start" }}>
        {/* Avatar with camera overlay */}
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={displayImage}
            sx={{
              width: 110, height: 110,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              color: "#fff",
              fontSize: 36, fontWeight: 800,
              border: `3px solid ${C.paper}`,
              boxShadow: `0 6px 20px ${C.primary}30`,
            }}
          >
            {!displayImage && initials}
          </Avatar>

          {/* Loading spinner overlay */}
          {uploading && (
            <Box sx={{
              position: "absolute", inset: 0, borderRadius: "50%",
              bgcolor: "rgba(13,17,23,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CircularProgress size={32} sx={{ color: "#fff" }} />
            </Box>
          )}

          {/* Camera button — bottom right */}
          <Tooltip title={currentImage ? "Change picture" : "Upload picture"}>
            <IconButton
              onClick={triggerFilePick}
              disabled={uploading}
              sx={{
                position: "absolute", bottom: 0, right: 0,
                width: 36, height: 36,
                bgcolor: C.primary, color: "#fff",
                border: `3px solid ${C.paper}`,
                "&:hover": { bgcolor: C.primaryDark },
                "&.Mui-disabled": { bgcolor: C.primary, opacity: 0.6, color: "#fff" },
              }}
            >
              <PhotoCamera sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Side text + actions */}
        <Box flex={1} sx={{ textAlign: { xs: "center", sm: "left" } }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.textPrimary, mb: 0.5, letterSpacing: -0.2 }}>
            Profile Picture
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.textSecondary, mb: 2, lineHeight: 1.6, maxWidth: 380 }}>
            Upload a clear photo of yourself. JPG, PNG, or WebP — up to 3MB.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
              {success}
            </Alert>
          )}

          <Stack direction="row" spacing={1.25} justifyContent={{ xs: "center", sm: "flex-start" }} flexWrap="wrap" gap={1}>
            <Button
              variant="contained"
              startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
              onClick={triggerFilePick}
              disabled={uploading}
              sx={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                color: "#fff",
                textTransform: "none", fontWeight: 700, fontSize: 13,
                borderRadius: 2.5, px: 2, py: 0.875,
                boxShadow: "none",
                "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
                "&.Mui-disabled": { background: "#CCC", color: "#fff" },
              }}
            >
              {currentImage ? "Change Picture" : "Upload Picture"}
            </Button>

            {currentImage && (
              <Button
                variant="outlined"
                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                onClick={handleRemove}
                disabled={uploading}
                sx={{
                  borderColor: C.errorSoft, color: C.error,
                  textTransform: "none", fontWeight: 700, fontSize: 13,
                  borderRadius: 2.5, px: 2, py: 0.875,
                  "&:hover": { borderColor: C.error, bgcolor: C.errorSoft },
                }}
              >
                Remove
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function AccountSettings() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  });
  const loggedInUserId = user?.id;

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState("en");
  const [activeScreen, setActiveScreen] = useState("account");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Terms acceptance feedback
  const [accepted, setAccepted] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Profile feedback
  const [profileMessage, setProfileMessage] = useState("");
  const [profileMessageType, setProfileMessageType] = useState("success");

  // Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updating, setUpdating] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const [dataSharing, setDataSharing] = useState({
    analytics: true, marketing: false, thirdParty: false, personalizedAds: false,
  });

  const settingsList = [
    { id: "viewProfile",   label: "View Profile",     icon: <ViewCarousel /> },
    { id: "editProfile",   label: "Edit Profile",     icon: <Edit /> },
    { id: "changePassword",label: "Change Password",  icon: <Lock /> },
    { id: "language",      label: "Language Settings",icon: <Language /> },
    { id: "availability",  label: "Availability",     icon: <Security /> },
    { id: "account",       label: "Account Settings", icon: <ScheduleOutlined /> },
    { id: "permissions",   label: "Data & Sharing",   icon: <Policy /> },
    // { id: "notifications", label: "Notifications",    icon: <Notifications /> },
    { id: "help",          label: "Help & Support",   icon: <Help /> },
    { id: "faq",           label: "FAQ",              icon: <Info /> },
    { id: "contact",       label: "Contact Us",       icon: <ContactMail /> },
    { id: "terms",         label: "Terms of Service", icon: <Gavel /> },
    { id: "privacy",       label: "Privacy Policy",   icon: <Policy /> },
    { id: "about",         label: "About Us",         icon: <Info /> },
  ];

  const activeLabel = settingsList.find((s) => s.id === activeScreen)?.label || "Settings";

  const goToScreen = (screen) => {
    setActiveScreen(screen);
    setProfileMessage("");
    setPwError("");
    setPwSuccess("");
    if (isMobile) setSidebarOpen(false);
  };

  // ── Avatar callbacks ──
  // After uploading or removing the avatar, update profileData so the View Profile
  // screen reflects the change immediately. Also refresh `user` from localStorage
  // which the AvatarUploader has already updated.
  const handleAvatarUpdated = (newPath) => {
    setProfileData((prev) => ({ ...prev, profileImage: newPath }));
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(stored);
    } catch { /* non-fatal */ }
  };

  const handleAvatarRemoved = () => {
    setProfileData((prev) => ({ ...prev, profileImage: "" }));
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      setUser(stored);
    } catch { /* non-fatal */ }
  };

  // fetch profile for edit (and for the main Account screen, since it now
  // shows the phone number too — it needs the same Learner/Mentor record)
  useEffect(() => {
    if ((activeScreen !== "editProfile" && activeScreen !== "account") || !user) return;
    const fetchProfile = async () => {
      setLoading(true);
      try {
        if (user.role === "learner") {
          const res = await fetch(`${API}/api/learner/${user.id}`);
          if (res.status === 404) { setLoading(false); return; }
          const data = await res.json();
          setProfileData(data);
        }
        if (user.role === "mentor") {
          const res = await fetch(`${API}/api/mentor/${user.id}`);
          const data = await res.json();
          setProfileData(data);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScreen]);

  // fetch profile for view
  useEffect(() => {
    if (activeScreen !== "viewProfile" || !loggedInUserId || !user?.role) return;
    const loadProfile = async () => {
      setLoading(true);
      try {
        const endpoint = user.role === "learner"
          ? `${API}/api/learner/list`
          : `${API}/api/mentor/list`;
        const res = await axios.get(endpoint);
        const list = res.data?.learners || res.data?.mentors || [];
        const profile = list.find((p) => p.userId === loggedInUserId);
        if (!profile) { setProfileMessage("Profile not found."); setProfileMessageType("error"); return; }
        // Merge in profileImage from the user object (since the list endpoint
        // may not include it — the source of truth is on the User model)
        setProfileData({ ...profile, profileImage: profile.profileImage || user.profileImage || "" });
      } catch (err) {
        setProfileMessage("Failed to load profile.");
        setProfileMessageType("error");
      }
      finally { setLoading(false); }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScreen, loggedInUserId, user?.role]);

  const handleChange = (field, value) => setProfileData((prev) => ({ ...prev, [field]: value }));

  const handleLearnerUpdate = async () => {
    try {
      setLoading(true);
      setProfileMessage("");
      await fetch(`${API}/api/learner/update/${user.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: profileData.phoneNumber, location: profileData.location,
          gender: profileData.gender, educationLevel: profileData.educationLevel,
          fieldOfInterest: profileData.fieldOfInterest || [], goals: profileData.goals,
          preferredMentorType: profileData.preferredMentorType,
        }),
      });
      setProfileMessage("Profile updated successfully!");
      setProfileMessageType("success");
    } catch (err) {
      setProfileMessage(err.message || "Failed to update profile.");
      setProfileMessageType("error");
    }
    finally { setLoading(false); }
  };

  const handleMentorUpdate = async () => {
    try {
      setLoading(true);
      setProfileMessage("");
      await fetch(`${API}/api/mentor/update-profile/${user.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: profileData.phoneNumber, location: profileData.location,
          gender: profileData.gender, professionalBackground: profileData.professionalBackground,
          areasOfExpertise: profileData.areasOfExpertise || [], yearsOfExperience: profileData.yearsOfExperience,
        }),
      });
      setProfileMessage("Profile updated successfully!");
      setProfileMessageType("success");
    } catch (err) {
      setProfileMessage(err.message || "Failed to update profile.");
      setProfileMessageType("error");
    }
    finally { setLoading(false); }
  };

  const handleChangePassword = async () => {
    setPwError("");
    setPwSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError("Please fill in all fields.");
      return;
    }
    if (newPassword.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setPwError("Your new password must be different from the current one.");
      return;
    }
    if (!user?.id) {
      setPwError("User session not found. Please sign in again.");
      return;
    }

    try {
      setUpdating(true);
      const res = await fetch(`${API}/api/user/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          currentPassword,
          newPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.msg || data.error || "Failed to change password.");
      }

      setPwSuccess("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwError(err.message || "Error changing password.");
    } finally {
      setUpdating(false);
    }
  };

  const handleAcceptTerms = async () => {
    try {
      setLoading(true); setMessage(""); setIsError(false);
      const userId = user?.id;
      if (!userId) { setMessage("User not found."); setIsError(true); return; }
      const response = await axios.post(`${API}/api/terms/accept`, { userId });
      if (response.status === 200) {
        setAccepted(true); setMessage("✅ You have successfully accepted the Terms of Service.");
      }
    } catch (err) {
      if (err.response?.data?.error === "Terms already accepted.") {
        setAccepted(true); setMessage("✅ You already accepted the Terms of Service.");
      } else { setMessage("Failed to accept Terms. Please try again."); setIsError(true); }
    } finally { setLoading(false); }
  };

  const AcceptButton = () => (
    <Box textAlign="center" mt={4}>
      {accepted ? (
        <Typography sx={{ color: C.secondary, fontWeight: 800 }}>{message}</Typography>
      ) : (
        <PrimaryButton disabled={loading} onClick={handleAcceptTerms} sx={{ px: 5, py: 1.25, fontSize: 14 }}>
          {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Accept & Continue"}
        </PrimaryButton>
      )}
      {message && !accepted && (
        <Typography sx={{ mt: 1.5, fontSize: 13, color: isError ? C.error : C.secondary, fontWeight: 600 }}>
          {message}
        </Typography>
      )}
    </Box>
  );

  // ── Sidebar list ─────────────────────────────────────────────────────────────
  const SidebarContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Paper elevation={0} sx={{
        position: "relative",
        p: 2, display: "flex", alignItems: "center",
        borderRadius: 3, mb: 2.5,
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
        color: "#fff",
        overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute",
          top: -60, right: -40,
          width: 140, height: 140,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <Avatar
          src={resolveImageUrl(user?.profileImage)}
          sx={{
            position: "relative", zIndex: 1,
            width: 46, height: 46, mr: 1.5,
            bgcolor: "#fff", color: C.primary,
            fontSize: 18, fontWeight: 800,
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          }}
        >
          {!user?.profileImage && (user?.email?.charAt(0).toUpperCase() || "U")}
        </Avatar>
        <Box minWidth={0} sx={{ position: "relative", zIndex: 1 }}>
          <Chip
            label={user?.role || "Guest"}
            size="small"
            sx={{
              bgcolor: alpha(C.secondary, 0.85), color: "#fff",
              height: 20, fontSize: 10, fontWeight: 800,
              textTransform: "capitalize", mb: 0.4,
            }}
          />
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.9)", fontWeight: 500 }} noWrap>
            {user?.email || "No email"}
          </Typography>
        </Box>
      </Paper>

      <Typography sx={{
        fontSize: 10, fontWeight: 700, color: C.textMuted,
        letterSpacing: 1, textTransform: "uppercase", mb: 1, px: 1,
      }}>
        Settings
      </Typography>
      <List dense disablePadding sx={{ flex: 1, overflowY: "auto" }}>
        {settingsList.map((item) => {
          const isActive = activeScreen === item.id;
          return (
            <ListItemButton
              key={item.id}
              selected={isActive}
              onClick={() => goToScreen(item.id)}
              sx={{
                borderRadius: 2, mb: 0.4, py: 0.85,
                "&.Mui-selected": {
                  bgcolor: C.primarySoft,
                  "&:hover": { bgcolor: C.primarySoft },
                },
                "&:hover": { bgcolor: C.primarySofter },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: isActive ? C.primary : C.textMuted }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography sx={{
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? C.primary : C.textSecondary,
                  }}>
                    {item.label}
                  </Typography>
                }
              />
              <ChevronRight sx={{ fontSize: 16, color: isActive ? C.primary : C.textMuted }} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  // ── Screen renderer ──────────────────────────────────────────────────────────
  const renderScreen = () => {
    switch (activeScreen) {

      case "viewProfile":
        return (
          <>
            {/* NEW: Avatar uploader at the top */}
            <SectionPaper accent="secondary">
              <AvatarUploader
                currentImage={profileData.profileImage || user?.profileImage}
                fallbackName={profileData.fullName || user?.fullName || user?.email}
                userId={user?.id}
                onUploadSuccess={handleAvatarUpdated}
                onRemoveSuccess={handleAvatarRemoved}
              />
            </SectionPaper>

            <SectionPaper>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, letterSpacing: -0.3 }}>
                  {user?.role === "learner" ? "Learner Profile" : "Mentor Profile"}
                </Typography>
                <Button
                  variant="outlined" onClick={() => setActiveScreen("editProfile")}
                  sx={{
                    borderRadius: 2.5, textTransform: "none", fontWeight: 700,
                    borderColor: C.primary, color: C.primary,
                    "&:hover": { borderColor: C.primaryDark, bgcolor: C.primarySoft },
                  }}
                >
                  Edit Profile
                </Button>
              </Stack>

              {profileMessage && (
                <Alert severity={profileMessageType} onClose={() => setProfileMessage("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
                  {profileMessage}
                </Alert>
              )}

              {loading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: C.primary }} /></Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={2.5}>
                  <ProfileItem label="Full Name" value={profileData.fullName} />
                  <ProfileItem label="Email" value={profileData.email} />
                  <ProfileItem label="Location" value={profileData.location} />
                  {user?.role === "learner" && (<>
                    <Divider sx={{ borderColor: C.borderSoft }} />
                    <ProfileItem label="Education Level" value={profileData.educationLevel} />
                    <ProfileItem label="Field of Interest" value={Array.isArray(profileData.fieldOfInterest) ? profileData.fieldOfInterest.join(", ") : "—"} />
                    <ProfileItem label="Goals" value={profileData.goals} multiline />
                    <ProfileItem label="Preferred Mentor Type" value={profileData.preferredMentorType} />
                  </>)}
                  {user?.role === "mentor" && (<>
                    <Divider sx={{ borderColor: C.borderSoft }} />
                    <ProfileItem label="Professional Background" value={profileData.professionalBackground} multiline />
                    <ProfileItem label="Areas of Expertise" value={Array.isArray(profileData.areasOfExpertise) ? profileData.areasOfExpertise.join(", ") : "—"} />
                    <ProfileItem label="Years of Experience" value={profileData.yearsOfExperience} />
                  </>)}
                </Box>
              )}
            </SectionPaper>
          </>
        );

      case "editProfile":
        return (
          <>
            {/* NEW: Avatar uploader at the top of Edit Profile too */}
            <SectionPaper accent="secondary">
              <AvatarUploader
                currentImage={profileData.profileImage || user?.profileImage}
                fallbackName={profileData.fullName || user?.fullName || user?.email}
                userId={user?.id}
                onUploadSuccess={handleAvatarUpdated}
                onRemoveSuccess={handleAvatarRemoved}
              />
            </SectionPaper>

            <SectionPaper>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
                {user?.role === "learner" ? "Edit Learner Profile" : "Edit Mentor Profile"}
              </Typography>

              {profileMessage && (
                <Alert severity={profileMessageType} onClose={() => setProfileMessage("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
                  {profileMessage}
                </Alert>
              )}

              {loading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: C.primary }} /></Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField label="Phone Number" fullWidth value={profileData.phoneNumber || ""} onChange={(e) => handleChange("phoneNumber", e.target.value)} sx={fieldSx} />
                  <TextField label="Location" fullWidth value={profileData.location || ""} onChange={(e) => handleChange("location", e.target.value)} sx={fieldSx} />
                  <TextField label="Gender" fullWidth value={profileData.gender || ""} onChange={(e) => handleChange("gender", e.target.value)} sx={fieldSx} />
                  {user?.role === "learner" && (<>
                    <Divider sx={{ borderColor: C.borderSoft }} />
                    <TextField label="Education Level" fullWidth value={profileData.educationLevel || ""} onChange={(e) => handleChange("educationLevel", e.target.value)} sx={fieldSx} />
                    <TextField label="Field of Interest (comma separated)" fullWidth value={Array.isArray(profileData.fieldOfInterest) ? profileData.fieldOfInterest.join(", ") : profileData.fieldOfInterest || ""} onChange={(e) => handleChange("fieldOfInterest", e.target.value.split(",").map((i) => i.trim()))} sx={fieldSx} />
                    <TextField label="Learning Goals" fullWidth multiline rows={3} value={profileData.goals || ""} onChange={(e) => handleChange("goals", e.target.value)} sx={fieldSx} />
                    <TextField label="Preferred Mentor Type" fullWidth value={profileData.preferredMentorType || ""} onChange={(e) => handleChange("preferredMentorType", e.target.value)} sx={fieldSx} />
                  </>)}
                  {user?.role === "mentor" && (<>
                    <Divider sx={{ borderColor: C.borderSoft }} />
                    <TextField label="Professional Background" fullWidth value={profileData.professionalBackground || ""} onChange={(e) => handleChange("professionalBackground", e.target.value)} sx={fieldSx} />
                    <TextField label="Areas of Expertise (comma separated)" fullWidth value={Array.isArray(profileData.areasOfExpertise) ? profileData.areasOfExpertise.join(", ") : profileData.areasOfExpertise || ""} onChange={(e) => handleChange("areasOfExpertise", e.target.value.split(",").map((i) => i.trim()))} sx={fieldSx} />
                    <TextField label="Years of Experience" type="number" fullWidth value={profileData.yearsOfExperience || ""} onChange={(e) => handleChange("yearsOfExperience", Number(e.target.value))} sx={fieldSx} />
                  </>)}
                  <PrimaryButton
                    size="large" disabled={loading}
                    onClick={user?.role === "learner" ? handleLearnerUpdate : handleMentorUpdate}
                  >
                    {loading ? "Saving..." : "Save Profile"}
                  </PrimaryButton>
                </Box>
              )}
            </SectionPaper>
          </>
        );

      case "changePassword":
        return (
          <SectionPaper sx={{ maxWidth: 500 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Lock sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 20 }, color: C.textPrimary, letterSpacing: -0.3 }}>
                Change Password
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 13, color: C.textSecondary, mb: 3 }}>
              Use at least 8 characters. We recommend a mix of letters, numbers, and symbols.
            </Typography>

            {pwError && (
              <Alert severity="error" onClose={() => setPwError("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
                {pwError}
              </Alert>
            )}
            {pwSuccess && (
              <Alert severity="success" onClose={() => setPwSuccess("")} sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}>
                {pwSuccess}
              </Alert>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                type="password"
                label="Current Password"
                fullWidth
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={updating}
                autoComplete="current-password"
                sx={fieldSx}
              />
              <TextField
                type="password"
                label="New Password"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={updating}
                autoComplete="new-password"
                helperText="Minimum 8 characters"
                sx={fieldSx}
              />
              <TextField
                type="password"
                label="Confirm New Password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={updating}
                autoComplete="new-password"
                sx={fieldSx}
              />

              <PrimaryButton size="large" onClick={handleChangePassword} disabled={updating}>
                {updating
                  ? <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Updating…</span>
                    </Stack>
                  : "Change Password"}
              </PrimaryButton>

              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
                  setPwError(""); setPwSuccess("");
                  setActiveScreen("account");
                }}
                disabled={updating}
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontWeight: 700,
                  borderColor: C.border, color: C.textSecondary,
                  "&:hover": { borderColor: C.textSecondary, bgcolor: C.borderSoft },
                }}
              >
                Cancel
              </Button>
            </Box>
          </SectionPaper>
        );

      case "language":
        return (
          <SectionPaper sx={{ maxWidth: 600 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Language & Region
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Display Language</Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              {[{ val: "en", label: "English", sub: "English (United States)" }, { val: "sw", label: "Kiswahili", sub: "Swahili (Kenya)" }].map((lang) => {
                const active = language === lang.val;
                return (
                  <Paper
                    key={lang.val} elevation={0}
                    onClick={() => setLanguage(lang.val)}
                    sx={{
                      p: 2, borderRadius: 2.5, cursor: "pointer",
                      border: `${active ? 2 : 1}px solid ${active ? C.primary : C.border}`,
                      bgcolor: active ? C.primarySoft : C.paper,
                      transition: "all 0.2s ease",
                      "&:hover": { borderColor: C.primary },
                    }}
                  >
                    <Typography sx={{ fontWeight: active ? 800 : 600, fontSize: 14, color: C.textPrimary }}>{lang.label}</Typography>
                    <Typography sx={{ fontSize: 12, color: C.textSecondary }}>{lang.sub}</Typography>
                  </Paper>
                );
              })}
            </Box>
            <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Timezone</Typography>
            <TextField select fullWidth value="Africa/Nairobi" SelectProps={{ native: true }} sx={{ ...fieldSx, maxWidth: 400 }}>
              <option value="Africa/Nairobi">Nairobi, Kenya (EAT)</option>
              <option value="Africa/Dar_es_Salaam">Dar es Salaam (EAT)</option>
              <option value="Africa/Kampala">Kampala, Uganda (EAT)</option>
            </TextField>
            <PrimaryButton sx={{ mt: 3 }}>Save Changes</PrimaryButton>
          </SectionPaper>
        );

      case "availability":
        return (
          <SectionPaper sx={{ maxWidth: 700 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 1.5, letterSpacing: -0.3 }}>
              Availability Settings
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.textSecondary, mb: 3 }}>
              {user?.role === "mentor" ? "Manage when you're available for sessions, meetings, or messages." : "View mentor availability and manage your meeting times."}
            </Typography>
            <PrimaryButton size="large" onClick={() => navigate(user?.role === "mentor" ? "/availability" : "/meeting")}>
              {user?.role === "mentor" ? "Open Availability" : "Go to Meetings"}
            </PrimaryButton>
          </SectionPaper>
        );

      case "permissions":
        return (
          <SectionPaper sx={{ maxWidth: 700 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Data & Sharing Permissions
            </Typography>
            {[
              { label: "Usage Analytics", desc: "Help us improve by sharing usage data", key: "analytics" },
              { label: "Marketing Communications", desc: "Receive updates, promotions, and newsletters", key: "marketing" },
              { label: "Share with Trusted Partners", desc: "Allow partners to provide relevant services", key: "thirdParty" },
              { label: "Personalized Advertising", desc: "Show ads based on your activity", key: "personalizedAds" },
            ].map(({ label, desc, key }) => (
              <Box key={key}>
                <ToggleRow label={label} description={desc} checked={dataSharing[key]} onChange={() => setDataSharing((p) => ({ ...p, [key]: !p[key] }))} />
                <Divider sx={{ borderColor: C.borderSoft }} />
              </Box>
            ))}
            <Stack direction="row" spacing={1.5} mt={3} flexWrap="wrap" gap={1}>
              <PrimaryButton>Save Preferences</PrimaryButton>
              <Button variant="text" sx={{ textTransform: "none", color: C.primary, fontWeight: 700 }}>Learn more</Button>
            </Stack>
          </SectionPaper>
        );

      case "notifications":
        return (
          <SectionPaper sx={{ maxWidth: 700 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Notification Settings
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Email Notifications</Typography>
            {[
              { label: "Learning Updates", desc: "New courses and learning materials", def: true },
              { label: "Messages & Feedback", desc: "Messages from mentors and peers", def: true },
              { label: "Account Activity", desc: "Security alerts and account changes", def: true },
            ].map(({ label, desc, def }) => (
              <Box key={label}>
                <ToggleRow label={label} description={desc} checked={def} onChange={() => {}} />
                <Divider sx={{ borderColor: C.borderSoft }} />
              </Box>
            ))}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5, mt: 3 }}>Push Notifications</Typography>
            {[
              { label: "Reminders", desc: "Reminders for assignments and deadlines", def: true },
              { label: "Achievements", desc: "Celebrate your milestones", def: false },
            ].map(({ label, desc, def }) => (
              <Box key={label}>
                <ToggleRow label={label} description={desc} checked={def} onChange={() => {}} />
                <Divider sx={{ borderColor: C.borderSoft }} />
              </Box>
            ))}
            <PrimaryButton sx={{ mt: 3 }}>Save Preferences</PrimaryButton>
          </SectionPaper>
        );

      case "help":
        return (
          <SectionPaper sx={{ maxWidth: 700 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Help & Support
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Getting Started</Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              {["How to Create Your Profile", "Finding Courses & Mentors", "Understanding Your Dashboard"].map((t) => (
                <InfoCard key={t} title={t} description="Click to learn more" />
              ))}
            </Box>
            <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Common Issues</Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              {["I Can't Log In", "Payment & Billing Questions", "Technical Issues"].map((t) => (
                <InfoCard key={t} title={t} description="Click for troubleshooting steps" />
              ))}
            </Box>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.primarySofter }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MailOutline sx={{ color: C.primary }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary }}>Email Us</Typography>
                  <Typography sx={{ fontSize: 12, color: C.textSecondary }}>support@globalimpact.org</Typography>
                </Box>
              </Stack>
            </Paper>
          </SectionPaper>
        );

      case "faq":
        return (
          <SectionPaper sx={{ maxWidth: 700 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Frequently Asked Questions
            </Typography>
            {[
              { section: "Account & Registration", items: [
                { q: "How do I create an account?", a: "Visit our signup page, enter your email, create a password, and verify it. Then complete your profile." },
                { q: "Can I change my email address?", a: "Yes, in Account Settings. You'll need to verify the new email before the change takes effect." },
                { q: "What if I forget my password?", a: "Click \"Forgot Password\" on the login page and follow the email instructions." },
              ]},
              { section: "Learning & Courses", items: [
                { q: "How do I enroll in a course?", a: "Browse courses, click \"Enroll\", and complete the payment if applicable. You'll have instant access." },
                { q: "Can I get a refund?", a: "We offer a 30-day money-back guarantee. Contact our support team to request one." },
                { q: "How do I connect with a mentor?", a: "Browse mentor profiles, review their expertise, and send a connection request." },
              ]},
            ].map(({ section, items }) => (
              <Box key={section} mb={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>{section}</Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {items.map(({ q, a }) => (
                    <Paper key={q} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.paper }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5, color: C.textPrimary }}>{q}</Typography>
                      <Typography sx={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.6 }}>{a}</Typography>
                    </Paper>
                  ))}
                </Box>
                <Divider sx={{ mt: 2.5, borderColor: C.borderSoft }} />
              </Box>
            ))}
            <PrimaryButton fullWidth>Contact Support Team</PrimaryButton>
          </SectionPaper>
        );

      case "contact":
        return (
          <SectionPaper sx={{ maxWidth: 600 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 1.5, letterSpacing: -0.3 }}>
              Contact Us
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.textSecondary, mb: 3 }}>Our support team is here to help you.</Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              {[
                { icon: <MailOutline sx={{ color: C.primary }} />, label: "Email Us", val: "support@globalimpact.org" },
                { icon: <Phone sx={{ color: C.primary }} />, label: "Call Us", val: "+254 700 000000", sub: "Mon–Fri, 9AM–5PM EAT" },
                { icon: <Help sx={{ color: C.primary }} />, label: "Help Center", val: "Visit our help center for FAQs and guides" },
              ].map(({ icon, label, val, sub }) => (
                <Paper key={label} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.paper }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    {icon}
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary }}>{label}</Typography>
                      <Typography sx={{ fontSize: 12, color: C.textSecondary }}>{val}</Typography>
                      {sub && <Typography sx={{ fontSize: 11, color: C.textMuted }}>{sub}</Typography>}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Box>
            <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Office Locations</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {[
                { city: "Nairobi, Kenya", addr: "ABC Place, 5th Floor\nWaiyaki Way, Westlands" },
                { city: "Kampala, Uganda", addr: "Speke Road, 3rd Floor\nKampala Central" },
              ].map(({ city, addr }) => (
                <Paper key={city} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.paper, flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5, color: C.textPrimary }}>{city}</Typography>
                  <Typography sx={{ fontSize: 12, color: C.textSecondary, whiteSpace: "pre-line" }}>{addr}</Typography>
                </Paper>
              ))}
            </Box>
          </SectionPaper>
        );

      case "terms":
        return (
          <SectionPaper sx={{ maxWidth: 800 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Terms of Service
            </Typography>
            <Box sx={{ maxHeight: 480, overflowY: "auto", pr: 1 }}>
              {[
                { title: "1. Acceptance of Terms", body: "By accessing and using Global Impact Innovators, you accept and agree to be bound by the terms and provision of this agreement." },
                { title: "2. Use License", body: "Permission is granted to temporarily download materials for personal, non-commercial viewing only. You may not modify, copy, or use materials for commercial purposes." },
                { title: "3. Disclaimer", body: "The materials are provided on an 'as is' basis. Global Impact Innovators makes no warranties, expressed or implied." },
                { title: "4. Limitations", body: "In no event shall Global Impact Innovators or its suppliers be liable for any damages arising from use or inability to use the materials." },
                { title: "5. Modifications", body: "Global Impact Innovators may revise these terms at any time without notice. By using this website, you agree to be bound by the current version." },
              ].map(({ title, body }) => (
                <Box key={title} mb={2.5}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.75, color: C.textPrimary }}>{title}</Typography>
                  <Typography sx={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              ))}
            </Box>
            <AcceptButton />
          </SectionPaper>
        );

      case "privacy":
        return (
          <SectionPaper sx={{ maxWidth: 800 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              Privacy Policy
            </Typography>
            <Box sx={{ maxHeight: 480, overflowY: "auto", pr: 1 }}>
              {[
                { title: "Introduction", body: "Global Impact Innovators operates this website. This page informs you of our policies regarding the collection, use, and disclosure of personal data." },
                { title: "Information Collection and Use", body: "We collect personal data (name, email, phone), usage data (browser, IP, pages visited), and cookies to provide and improve our Service." },
                { title: "Use of Data", body: "Data is used to provide and maintain our Service, notify you about changes, allow participation in interactive features, and provide customer support." },
                { title: "Security of Data", body: "We strive to use commercially acceptable means to protect your data, but cannot guarantee absolute security over Internet transmission." },
                { title: "Contact Us", body: "Questions about this Privacy Policy? Contact us at support@globalimpact.org" },
              ].map(({ title, body }) => (
                <Box key={title} mb={2.5}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.75, color: C.textPrimary }}>{title}</Typography>
                  <Typography sx={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              ))}
            </Box>
            <AcceptButton />
          </SectionPaper>
        );

      case "about":
        return (
          <SectionPaper sx={{ maxWidth: 800 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: C.textPrimary, mb: 3, letterSpacing: -0.3 }}>
              About Global Impact Innovators
            </Typography>
            {[
              { title: "Our Mission", body: "Democratizing education across Africa by connecting learners with expert mentors and quality resources." },
              { title: "Our Vision", body: "A thriving ecosystem of learning and mentorship that empowers individuals to achieve their full potential." },
            ].map(({ title, body }) => (
              <Box key={title} mb={3}>
                <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.75, color: C.textPrimary }}>{title}</Typography>
                <Typography sx={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>{body}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 2.5, borderColor: C.borderSoft }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>What We Do</Typography>
            <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
              {[
                { title: "Connect Learners with Mentors", description: "We facilitate connections between students and experienced professionals." },
                { title: "Quality Learning Resources", description: "Curated courses and materials designed for African learners." },
                { title: "Build Community", description: "A supportive space to collaborate, share, and grow together." },
              ].map((item) => <InfoCard key={item.title} {...item} />)}
            </Box>
            <Divider sx={{ my: 2.5, borderColor: C.borderSoft }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>Our Values</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
              {[["Accessibility", "Education for all"], ["Quality", "Excellence in learning"], ["Integrity", "Trust and transparency"], ["Impact", "Real change in lives"]].map(([v, sub]) => (
                <Paper key={v} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.primarySofter, flex: 1, minWidth: 150 }}>
                  <Typography sx={{ fontWeight: 800, color: C.primary, fontSize: 14 }}>{v}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.textSecondary }}>{sub}</Typography>
                </Paper>
              ))}
            </Box>
            <PrimaryButton onClick={() => setActiveScreen("contact")}>Contact Us</PrimaryButton>
          </SectionPaper>
        );

      case "account":
      default:
        return (
          <Box>
            {/* Hero header */}
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                borderRadius: 3, overflow: "hidden",
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                p: { xs: 3, sm: 4 },
                mb: { xs: 2.5, sm: 3 },
                color: "#fff",
                boxShadow: `0 6px 24px ${C.primary}30`,
              }}
            >
              <Box sx={{
                position: "absolute",
                top: "-40%", right: "-10%",
                width: { xs: 180, sm: 240 }, height: { xs: 180, sm: 240 },
                borderRadius: "50%",
                background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
                pointerEvents: "none",
              }} />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                  <SettingsIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
                  <Typography sx={{
                    fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
                  }}>
                    Account
                  </Typography>
                </Stack>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 30 },
                  color: "#fff", lineHeight: 1.15, mb: 0.5, letterSpacing: -0.5,
                }}>
                  Account Settings
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: 13, sm: 14 }, lineHeight: 1.5 }}>
                  Manage your login, security, and personal information
                </Typography>
              </Box>
            </Paper>

            {/* NEW: Avatar uploader on the main Account screen too — handy for quick changes */}
            <SectionPaper accent="secondary">
              <AvatarUploader
                currentImage={user?.profileImage}
                fallbackName={user?.fullName || user?.email}
                userId={user?.id}
                onUploadSuccess={handleAvatarUpdated}
                onRemoveSuccess={handleAvatarRemoved}
              />
            </SectionPaper>

            <SectionPaper>
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.textPrimary, mb: 2, letterSpacing: -0.2 }}>
                Login & Security
              </Typography>
              <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${C.borderSoft}`, bgcolor: C.primarySofter }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MailOutline sx={{ fontSize: 18, color: C.primary }} />
                    <Typography sx={{ fontSize: 13, color: C.textPrimary, fontWeight: 600 }}>
                      {user?.email || "No email"}
                    </Typography>
                  </Stack>
                  <IconButton size="small" sx={{ color: C.primary }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Stack>
              </Paper>
            </SectionPaper>

            <SectionPaper accent="secondary">
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.textPrimary, mb: 1.5, letterSpacing: -0.2 }}>
                Password Management
              </Typography>
              <ListItemButton
                onClick={() => setActiveScreen("changePassword")}
                sx={{
                  borderRadius: 2, mb: 0.5,
                  "&:hover": { bgcolor: C.primarySofter },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}><Lock sx={{ fontSize: 20, color: C.primary }} /></ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Change Password</Typography>} />
                <ChevronRight sx={{ fontSize: 16, color: C.textMuted }} />
              </ListItemButton>
              <Divider sx={{ mx: 1, borderColor: C.borderSoft }} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TwoWheeler sx={{ fontSize: 20, color: C.primary }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Two-Factor Authentication</Typography>
                </Stack>
                <Switch
                  checked={twoFactorEnabled}
                  onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
                  size="small"
                  sx={{
                    "& .Mui-checked": { color: `${C.primary} !important` },
                    "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${C.primary} !important` },
                  }}
                />
              </Box>
            </SectionPaper>

            {/* Phone Number — blank/placeholder until the user sets one via Edit Profile */}
            <SectionPaper>
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.textPrimary, mb: 1.5, letterSpacing: -0.2 }}>
                Phone Number
              </Typography>
              <ListItemButton
                onClick={() => setActiveScreen("editProfile")}
                sx={{ borderRadius: 2, "&:hover": { bgcolor: C.primarySofter } }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}><Phone sx={{ fontSize: 20, color: C.primary }} /></ListItemIcon>
                <ListItemText
                  primary={
                    profileData.phoneNumber ? (
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                        {profileData.phoneNumber}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textMuted, fontStyle: "italic" }}>
                        Not added yet — tap to add
                      </Typography>
                    )
                  }
                />
                <ChevronRight sx={{ fontSize: 16, color: C.textMuted }} />
              </ListItemButton>
            </SectionPaper>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", bgcolor: C.bg, minHeight: "100vh" }}>
      <TopBar />

      {/* Mobile top bar */}
      {isMobile && (
        <Box sx={{
          display: "flex", alignItems: "center",
          px: 2, py: 1.5,
          bgcolor: C.paper,
          borderBottom: `1px solid ${C.border}`,
          gap: 1,
        }}>
          <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ color: C.primary }}>
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.textPrimary, flex: 1, letterSpacing: -0.2 }} noWrap>
            {activeLabel}
          </Typography>
        </Box>
      )}

      {/* Mobile drawer */}
      <Drawer
        anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        PaperProps={{ sx: { width: 290, p: 2.5, bgcolor: C.paper, display: "flex", flexDirection: "column" } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: C.textPrimary }}>Settings</Typography>
          <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: C.textMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <SidebarContent />
      </Drawer>

      {/* Main layout */}
      <Box sx={{
        flex: 1, display: "flex",
        flexDirection: { xs: "column", md: "row" },
        px: { xs: 2, sm: 3, md: 4, lg: 8 },
        py: { xs: 3, md: 4 },
        gap: 3, alignItems: "flex-start",
      }}>
        {!isMobile && (
          <Paper elevation={0} sx={{
            width: 280, flexShrink: 0, p: 2.5,
            borderRadius: 3, border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            position: "sticky", top: 24,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}>
            <SidebarContent />
          </Paper>
        )}

        <Box flex={1} minWidth={0}>{renderScreen()}</Box>
      </Box>

      <Footer />
    </Box>
  );
}